/**
 * The organization's known objects: the list, one identity with its embeddings, and the
 * matching thresholds.
 *
 * One fetch of the list serves every reader: a wall of nine cameras mounts nine overlays. Nothing
 * on the notifications stream says an identity changed, so each editor names its write through
 * `enrollmentsChangedAtom`, and every holder of enrollment data follows that change.
 */

import { type Atom, atom, type WritableAtom } from 'jotai';
import { atomFamily } from 'jotai-family';

import { snapshotAtoms, snapshotMemberAtom } from '../snapshotAtoms';
import { api } from '@/api';
import { MINUTE_MS, SECOND_MS } from '@/lib/timeUnits';
import {
    type Enrollment, type EnrollmentChange, EnrollmentChangeKind, type EnrollmentChangeNotice,
    type EnrollmentDetailSnapshot, type EnrollmentSettingsSnapshot, type EnrollmentsSnapshot,
} from '@/types/enrollments';
import { SnapshotCommand, type SnapshotWrite } from '@/types/snapshot';

import { applyEnrollmentChangeToSearchesAtom } from './eventSearchAtoms';
import { applyEnrollmentChangeToSimilarObjectsAtom } from './similarObjectsAtoms';

export const FIRST_RETRY_AFTER_MS = 5 * SECOND_MS;
export const LONGEST_RETRY_AFTER_MS = 2 * MINUTE_MS;

const PENDING: EnrollmentsSnapshot = { enrollments: [], isLoading: true, error: null };

/** Resolves after `delayMs`, or as soon as `signal` aborts. */
function waitFor(delayMs: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
        // An abort that already happened fires no event.
        if (signal.aborted) {
            resolve();
            return;
        }
        const timer = setTimeout(resolve, delayMs);
        signal.addEventListener('abort', () => {
            clearTimeout(timer);
            resolve();
        }, { once: true });
    });
}

/**
 * Asks until the list answers. An overlay without colours is still an overlay, but the box's
 * menu without the list has no "add to …" actions, and nothing else would ask again before
 * the next identity changes.
 */
async function listUntilAnswered(
    signal: AbortSignal, retryAfterMs = FIRST_RETRY_AFTER_MS,
): Promise<Enrollment[]> {
    try {
        return await api.listEnrollments();
    } catch (error) {
        await waitFor(retryAfterMs, signal);
        if (signal.aborted) {
            throw error;
        }
        return listUntilAnswered(signal, Math.min(retryAfterMs * 2, LONGEST_RETRY_AFTER_MS));
    }
}

const snapshot = snapshotAtoms<EnrollmentsSnapshot>(PENDING, async (signal) => ({
    enrollments: await listUntilAnswered(signal),
    isLoading: false,
    error: null,
}));

export const enrollmentsAtom = snapshot.value;
export const refreshEnrollmentsAtom = snapshot.reloadNow;

const NO_DETAIL: EnrollmentDetailSnapshot = { enrollment: null, isLoading: true, error: null };

/**
 * One identity with the vectors of its exemplars: read it for the snapshot, set it to command.
 * A dialog that opens again seeds its search from these vectors, so it waits for its reload.
 */
export const enrollmentDetailAtom = atomFamily((id: string) => snapshotMemberAtom(
    snapshotAtoms<EnrollmentDetailSnapshot>(NO_DETAIL, async () => ({
        enrollment: await api.getEnrollment(id, { withEmbeddings: true }),
        isLoading: false,
        error: null,
    }), { isStaleWhenUnread: true }),
));

const NONE_OPEN: EnrollmentDetailSnapshot = { enrollment: null, isLoading: false, error: null };

/** What a reader with no identity reads; there is nothing to load, so a command does nothing. */
export const noEnrollmentDetailAtom:
WritableAtom<EnrollmentDetailSnapshot, [SnapshotWrite<EnrollmentDetailSnapshot>], void> = atom(
    (): EnrollmentDetailSnapshot => NONE_OPEN,
    (_get, _set, _write: SnapshotWrite<EnrollmentDetailSnapshot>): void => undefined,
);

const NO_SETTINGS: EnrollmentSettingsSnapshot = { settings: null, isLoading: true, error: null };

// A dialog that opens again must not take the last open's answer, or its error, for this one's.
const settings = snapshotAtoms<EnrollmentSettingsSnapshot>(NO_SETTINGS, async () => ({
    settings: await api.getEnrollmentSettings(),
    isLoading: false,
    error: null,
}), { isStaleWhenUnread: true });

export const enrollmentSettingsAtom = settings.value;
export const answerEnrollmentSettingsAtom = settings.answer;

/** What a closed thresholds dialog reads, so that it requests nothing. */
export const noEnrollmentSettingsAtom: Atom<EnrollmentSettingsSnapshot> = atom(
    (): EnrollmentSettingsSnapshot => ({ settings: null, isLoading: false, error: null }),
);

export const enrollmentChangeAtom = atom<EnrollmentChangeNotice | null>(null);

const RELOAD: SnapshotWrite<EnrollmentDetailSnapshot> = { command: SnapshotCommand.Reload };

/** An editor names each write here; every holder of enrollment data follows it. */
export const enrollmentsChangedAtom = atom(null, (get, set, change: EnrollmentChange): void => {
    set(snapshot.reload);
    switch (change.kind) {
        case EnrollmentChangeKind.Enrolled:
        case EnrollmentChangeKind.Edited:
            // Only an identity a dialog opened has a member; a change must not make one.
            if ([...enrollmentDetailAtom.getParams()].includes(change.enrollmentId)) {
                set(enrollmentDetailAtom(change.enrollmentId), RELOAD);
            }
            break;
        default:
            break;
    }
    set(applyEnrollmentChangeToSearchesAtom, change);
    set(applyEnrollmentChangeToSimilarObjectsAtom, change);
    set(enrollmentChangeAtom, { seq: (get(enrollmentChangeAtom)?.seq ?? 0) + 1, change });
});

const rowsAtom = atom((get): Enrollment[] => get(enrollmentsAtom).enrollments);

/** enrollmentId → the identity, as the list answers it. */
export const enrollmentStylesAtom = atom((get): ReadonlyMap<string, Enrollment> => {
    return new Map(get(rowsAtom).map((enrollment) => [enrollment.enrollmentId, enrollment]));
});
